import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';

export interface UserStatusDialogProps {
  variant: 'deactivate' | 'reactivate' | 'demote';
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  userName: string;
  loading?: boolean;
}

const CONFIG = {
  deactivate: {
    title: 'Deactivate User',
    body: (name: string) =>
      <>Are you sure you want to deactivate <strong>{name}</strong>? This user will no longer be able to access the system. You can reactivate them later if needed.</>,
    confirmLabel: 'Deactivate',
    loadingLabel: 'Deactivating...',
    confirmColor: 'error' as const,
  },
  reactivate: {
    title: 'Reactivate User',
    body: (name: string) =>
      <>Are you sure you want to reactivate <strong>{name}</strong>? This will restore their access to the system with their previous permissions.</>,
    confirmLabel: 'Reactivate',
    loadingLabel: 'Reactivating...',
    confirmColor: 'success' as const,
  },
  demote: {
    title: 'Demote to Driver Only',
    body: (name: string) =>
      <>Are you sure you want to demote <strong>{name}</strong> to driver only? They will lose portal access and can only use the mobile app. Their driver record and market links will be preserved.</>,
    confirmLabel: 'Demote to Driver',
    loadingLabel: 'Demoting...',
    confirmColor: 'warning' as const,
  },
} as const;

const UserStatusDialog: React.FC<UserStatusDialogProps> = ({
  variant,
  open,
  onClose,
  onConfirm,
  userName,
  loading = false,
}) => {
  const cfg = CONFIG[variant];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{cfg.title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2">{cfg.body(userName)}</Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button variant="outlined" color="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button color={cfg.confirmColor} onClick={onConfirm} disabled={loading}>
          {loading ? cfg.loadingLabel : cfg.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserStatusDialog;
